import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-10475c82"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "custom-reply-page" }

import { computed, onMounted, ref } from 'vue'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { DataAnalysis, Download } from '@element-plus/icons-vue'
import { CrudSearch, CrudTable } from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import { useStore } from '@/store'
import localCache from '@/utils/cache'
import {
  createCustomReplySearchItems,
  customReplyTableColumns
} from './custom-reply.config'
import {
  exportCustomReplyDetail,
  exportCustomReplySummary,
  fetchCustomReplyPage,
  fetchCustomReplyUsedBy
} from './custom-reply.service'
import type {
  CustomReplyFilters,
  CustomReplyRecord
} from './custom-reply.service'

interface SelectOption {
  label: string
  value: string | number
}

interface Company {
  id: string | number
  name: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomReply',
  setup(__props) {

const store = useStore()
const canList = usePermission('customReply', 'list')
const canDownload = usePermission('customReply', 'download')
const canSummary = usePermission('customReply', 'summary')

function createFilters(): CustomReplyFilters {
  const today = dayjs().format('YYYY-MM-DD')
  return {
    userId: '',
    corPid: '20',
    userName: '',
    usedBy:
      store.state.login.userInfo.isManager != '1'
        ? store.state.login.userInfo.realName
        : '',
    nickName: '',
    timeSlot: [today, today],
    replyStatus: 0,
    unrepliedStatus: ''
  }
}

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  lastSuccessfulQuery,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  changeSort,
  refresh
} = usePagedCrud({
  createFilters,
  fetchPage: fetchCustomReplyPage
})

const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)

const companyOptions = (localCache.getCache('companyData') as Company[]).map(
  (item) => ({ label: item.name, value: item.id })
)
const usedByOptions = ref<SelectOption[]>([])
const optionsLoading = ref(false)
const detailExporting = ref(false)
const summaryExporting = ref(false)

function handleReplyStatusChange(value: number | string) {
  if (value !== 0) filters.unrepliedStatus = ''
}

const searchItems = computed(() =>
  createCustomReplySearchItems({
    companyOptions,
    usedByOptions: usedByOptions.value,
    optionsLoading: optionsLoading.value,
    onReplyStatusChange: handleReplyStatusChange
  })
)

function handleSortChange(value: {
  prop?: string
  order?: 'ascending' | 'descending' | null
}) {
  return value.prop && value.order
    ? changeSort({ prop: value.prop, order: value.order })
    : changeSort()
}

function formatReplyDual(replyDual: number | null, replyDualStr: string) {
  if (replyDual == null) return replyDualStr || '尚未回复'

  const seconds = Math.floor(replyDual / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  return `${days}天${hours % 24}时${minutes % 60}分${seconds % 60}秒`
}

function replyDualColor(replyDual: number | null, replyDualStr: string) {
  if (replyDual == null) {
    return replyDualStr === '超出一小时未回复' ? 'red' : 'blue'
  }
  if (replyDual > 24 * 60 * 60 * 1000) return 'red'
  if (replyDual > 60 * 60 * 1000) return 'orange'
  if (replyDual > 30 * 60 * 1000) return 'darksalmon'
  return undefined
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

async function downloadDetail() {
  detailExporting.value = true
  try {
    saveBlob(
      await exportCustomReplyDetail(lastSuccessfulQuery.value!),
      '客户回复明细表.xlsx'
    )
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '明细导出失败')
  } finally {
    detailExporting.value = false
  }
}

async function downloadSummary() {
  summaryExporting.value = true
  try {
    saveBlob(
      await exportCustomReplySummary(lastSuccessfulQuery.value!),
      '业务员回复情况汇总.xlsx'
    )
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '汇总导出失败')
  } finally {
    summaryExporting.value = false
  }
}

async function loadUsedByOptions() {
  optionsLoading.value = true
  try {
    usedByOptions.value = (await fetchCustomReplyUsedBy()).map((item) => ({
      label: item.userName,
      value: item.userName
    }))
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '使用人加载失败')
  } finally {
    optionsLoading.value = false
  }
}

onMounted(() => {
  if (canList) load()
  loadUsedByOptions()
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: searchItems.value,
      loading: _unref(loading),
      onSearch: _unref(search),
      onReset: _unref(reset)
    }, null, 8, ["model", "items", "loading", "onSearch", "onReset"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(customReplyTableColumns),
      onPageChange: _unref(changePage),
      onPageSizeChange: _unref(changePageSize),
      onSortChange: handleSortChange,
      onRetry: _unref(refresh)
    }, {
      title: _withCtx(() => [
        _createTextVNode("回复列表")
      ]),
      actions: _withCtx(() => [
        (_unref(canDownload))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              loading: detailExporting.value,
              disabled: summaryExporting.value || !_unref(lastSuccessfulQuery),
              onClick: downloadDetail
            }, {
              default: _withCtx(() => [
                (!detailExporting.value)
                  ? (_openBlock(), _createBlock(_unref(Download), {
                      key: 0,
                      class: "custom-reply-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 下载 ")
              ]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true),
        (_unref(canSummary))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 1,
              type: "primary",
              loading: summaryExporting.value,
              disabled: detailExporting.value || !_unref(lastSuccessfulQuery),
              onClick: downloadSummary
            }, {
              default: _withCtx(() => [
                (!summaryExporting.value)
                  ? (_openBlock(), _createBlock(_unref(DataAnalysis), {
                      key: 0,
                      class: "custom-reply-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 汇总 ")
              ]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true)
      ]),
      replyDual: _withCtx((scope) => [
        _createElementVNode("span", {
          style: _normalizeStyle({
            color: replyDualColor(
              (scope.row as CustomReplyRecord).replyDual,
              (scope.row as CustomReplyRecord).replyDualStr
            )
          })
        }, _toDisplayString(formatReplyDual(
              (scope.row as CustomReplyRecord).replyDual,
              (scope.row as CustomReplyRecord).replyDualStr
            )), 5)
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns", "onPageChange", "onPageSizeChange", "onRetry"])
  ]))
}
}

})